import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import { marketplaceAPI, productsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Cart.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BUYER_PROTECTION = 1.5;

// TWINT temporairement désactivé (passer à true pour réactiver)
const TWINT_ENABLED = false;

const CartCheckoutForm = ({ cart, updateShippingMethod, removeFromCart, clearCart, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    postalCode: '',
    country: 'Suisse'
  });
  const [orderTotal, setOrderTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [validating, setValidating] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('card');

  // Valider la disponibilite des articles au chargement
  useEffect(() => {
    validateCartItems();
  }, []);

  const validateCartItems = async () => {
    setValidating(true);
    try {
      const items = cart.map(item => ({
        productId: item.id,
        shippingMethod: item.shippingMethod
      }));
      const response = await marketplaceAPI.calculateCartTotal(items);
      const { unavailableIds } = response.data;

      if (unavailableIds && unavailableIds.length > 0) {
        unavailableIds.forEach(id => removeFromCart(id));
        setError(t('cart.itemsRemoved', { count: unavailableIds.length }));
      }
      setOrderTotal(response.data);
    } catch (err) {
      setError(t('cart.validationError'));
    } finally {
      setValidating(false);
    }
  };

  // Recalculer quand les methodes de livraison changent
  const recalculate = useCallback(async () => {
    if (cart.length === 0) return;
    setCalculating(true);
    try {
      const items = cart.map(item => ({
        productId: item.id,
        shippingMethod: item.shippingMethod
      }));
      const response = await marketplaceAPI.calculateCartTotal(items);
      setOrderTotal(response.data);
      setError('');
    } catch (err) {
      // silently fail
    } finally {
      setCalculating(false);
    }
  }, [cart]);

  useEffect(() => {
    if (!validating && cart.length > 0) {
      recalculate();
    }
  }, [cart.map(i => i.shippingMethod).join(',')]);

  const needsShipping = cart.some(item => item.shippingMethod === 'shipping');

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const validateAddress = () => {
    if (!needsShipping) return true;
    const { name, street, city, postalCode } = shippingAddress;
    return name && street && city && postalCode;
  };

  const handleProceedToPayment = () => {
    if (!validateAddress()) {
      setError(t('buy.fillAddress'));
      return;
    }
    setError('');
    setStep(2);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe) return;

    setLoading(true);
    setError('');

    try {
      const items = cart.map(item => ({
        productId: item.id,
        shippingMethod: item.shippingMethod
      }));

      const intentResponse = await marketplaceAPI.createCartPurchaseIntent(
        items,
        needsShipping ? shippingAddress : null,
        paymentMethod
      );

      const { clientSecret, orderIds } = intentResponse.data;

      if (paymentMethod === 'twint') {
        const { error: stripeError } = await stripe.confirmTwintPayment(clientSecret, {
          payment_method: {
            billing_details: {
              name: shippingAddress.name || user?.username || '',
              email: user?.email || '',
            },
          },
          return_url: `${window.location.origin}/payment-success?type=cart`,
        });
        if (stripeError) {
          setError(stripeError.message);
          setLoading(false);
        }
        return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        await marketplaceAPI.confirmPurchase(paymentIntent.id);
        clearCart();
        onSuccess(orderIds);
      }
    } catch (err) {
      if (err.response?.data?.unavailableIds) {
        err.response.data.unavailableIds.forEach(id => removeFromCart(id));
        setError(t('cart.someUnavailable'));
        setStep(1);
      } else {
        setError(err.response?.data?.message || t('buy.paymentError'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return <div className="cart-loading">{t('cart.validating')}</div>;
  }

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <div className="cart-empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
        </div>
        <h3>{t('cart.empty')}</h3>
        <p>{t('cart.emptyHint')}</p>
        <button className="btn-primary btn-large" onClick={() => navigate('/')}>
          {t('cart.discoverItems')}
        </button>
      </div>
    );
  }

  return (
    <div className="cart-checkout-form">
      {step === 1 && (
        <>
          {/* Liste des articles */}
          <div className="cart-items">
            <h3>{t('cart.itemsCount', { count: cart.length })}</h3>
            {cart.map(item => {
              const imageUrl = item.images && item.images.length > 0
                ? `/uploads/thumbnails/${item.images[0]}` : null;
              const itemPrice = parseFloat(item.price);
              const shippingPrice = item.shippingMethod === 'shipping'
                ? parseFloat(item.shipping_price || 7) : 0;

              return (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-main">
                    {imageUrl ? (
                      <img src={imageUrl} alt={item.title} className="cart-item-image" />
                    ) : (
                      <div className="cart-item-image cart-item-no-image">?</div>
                    )}
                    <div className="cart-item-info">
                      <span className="cart-item-title">{item.title}</span>
                      <span className="cart-item-price">{itemPrice.toFixed(2)} CHF</span>
                      <span className="cart-item-seller">{t('cart.bySeller', { name: item.seller_username })}</span>
                    </div>
                    <button
                      className="cart-item-remove"
                      onClick={() => removeFromCart(item.id)}
                      aria-label={t('common.delete')}
                    >
                      &times;
                    </button>
                  </div>

                  {/* Choix livraison par article */}
                  <div className="cart-item-shipping">
                    {item.shipping_available ? (
                      <label className={`cart-shipping-option ${item.shippingMethod === 'shipping' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name={`shipping-${item.id}`}
                          value="shipping"
                          checked={item.shippingMethod === 'shipping'}
                          onChange={() => updateShippingMethod(item.id, 'shipping')}
                        />
                        <span>{t('cart.shippingOption', { price: parseFloat(item.shipping_price || 7).toFixed(2) })}</span>
                      </label>
                    ) : null}
                    {item.local_pickup ? (
                      <label className={`cart-shipping-option ${item.shippingMethod === 'pickup' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name={`shipping-${item.id}`}
                          value="pickup"
                          checked={item.shippingMethod === 'pickup'}
                          onChange={() => updateShippingMethod(item.id, 'pickup')}
                        />
                        <span>{t('cart.pickupOption', { city: item.city ? ` - ${item.city}` : '' })}</span>
                      </label>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Adresse de livraison */}
          {needsShipping && (
            <div className="cart-address">
              <h3>{t('buy.deliveryAddress')}</h3>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder={t('buy.fullName')}
                  value={shippingAddress.name}
                  onChange={handleAddressChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="street"
                  placeholder={t('buy.addressLabel')}
                  value={shippingAddress.street}
                  onChange={handleAddressChange}
                  required
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  name="postalCode"
                  placeholder={t('buy.postalCode')}
                  value={shippingAddress.postalCode}
                  onChange={handleAddressChange}
                  required
                />
                <input
                  type="text"
                  name="city"
                  placeholder={t('buy.city')}
                  value={shippingAddress.city}
                  onChange={handleAddressChange}
                  required
                />
              </div>
            </div>
          )}

          {/* Récapitulatif */}
          {orderTotal && (
            <div className="cart-price-breakdown">
              <h3>{t('buy.summary')}</h3>
              {orderTotal.items.map(item => (
                <div key={item.productId} className="cart-price-item">
                  <div className="price-line">
                    <span className="price-item-title">{item.title}</span>
                    <span>{item.productPrice.toFixed(2)} CHF</span>
                  </div>
                  {item.shippingPrice > 0 && (
                    <div className="price-line sub">
                      <span>{t('buy.shippingFee')}</span>
                      <span>{item.shippingPrice.toFixed(2)} CHF</span>
                    </div>
                  )}
                  <div className="price-line sub">
                    <span>{t('buy.protectionFee')}</span>
                    <span>{item.buyerProtection.toFixed(2)} CHF</span>
                  </div>
                  {item.serviceFee > 0 && (
                    <div className="price-line sub">
                      <span>{t('buy.serviceFee')}</span>
                      <span>{item.serviceFee.toFixed(2)} CHF</span>
                    </div>
                  )}
                </div>
              ))}
              <div className="price-line total">
                <span>{t('buy.total')}</span>
                <span>{calculating ? '...' : `${orderTotal.total.toFixed(2)} CHF`}</span>
              </div>
            </div>
          )}

          <div className="cart-protection-info">
            <span className="info-icon">&#128274;</span>
            <p>
              <strong>{t('buy.protectionFee')} :</strong> {t('buy.protectionInfo')}
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="btn-primary btn-large"
            onClick={handleProceedToPayment}
            disabled={!orderTotal || calculating}
          >
            {t('buy.continueToPayment')}
          </button>
        </>
      )}

      {step === 2 && (
        <form onSubmit={handlePayment}>
          <h3>{t('buy.securePayment')}</h3>

          <div className="payment-summary">
            <p>{t('buy.totalToPay', { amount: orderTotal?.total.toFixed(2) })}</p>
            <p className="payment-summary-count">{t('cart.itemsLabel', { count: cart.length, plural: cart.length > 1 ? 's' : '' })}</p>
          </div>

          <div className="payment-method-choice">
            <label className={`payment-method-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                checked={paymentMethod === 'card'}
                onChange={() => setPaymentMethod('card')}
              />
              <span className="payment-method-icon">&#128179;</span>
              <span>{t('buy.creditCard')}</span>
            </label>
            {TWINT_ENABLED && (
              <label className={`payment-method-option ${paymentMethod === 'twint' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="twint"
                  checked={paymentMethod === 'twint'}
                  onChange={() => setPaymentMethod('twint')}
                />
                <span className="payment-method-icon">&#128241;</span>
                <span translate="no">TWINT</span>
              </label>
            )}
          </div>

          {paymentMethod === 'card' && (
            <div className="card-element-container">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': { color: '#aab7c4' },
                    },
                    invalid: { color: '#9e2146' },
                  },
                }}
              />
            </div>
          )}

          {paymentMethod === 'twint' && (
            <div className="twint-info">
              <p>{t('buy.twintRedirect')}</p>
              <p>{t('buy.twintInstall')}</p>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              {t('buy.back')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!stripe || loading}
            >
              {loading ? t('buy.paying') : <span translate="no">{paymentMethod === 'twint' ? t('buy.payButtonTwint', { amount: orderTotal?.total.toFixed(2) }) : t('buy.payButton', { amount: orderTotal?.total.toFixed(2) })}</span>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, removeFromCart, updateShippingMethod, clearCart } = useCart();
  const [success, setSuccess] = useState(false);
  const [orderIds, setOrderIds] = useState([]);

  const handleSuccess = (ids) => {
    setOrderIds(ids);
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="cart-container">
        <div className="cart-card success-card">
          <div className="success-icon">&#10003;</div>
          <h2>{t('cart.ordersConfirmed')}</h2>
          <p>
            {t('cart.ordersPaymentAccepted')}
          </p>

          <div className="next-steps">
            <h3>{t('buy.nextSteps')}</h3>
            <ol>
              <li>{t('cart.cartStep1')}</li>
              <li>{t('cart.cartStep2')}</li>
              <li>{t('cart.cartStep3')}</li>
            </ol>
          </div>

          <div className="button-group">
            <button className="btn-primary" onClick={() => navigate('/my-purchases')}>
              {t('buy.viewPurchases')}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/')}>
              {t('buy.continueShopping')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-card">
        <h2>{t('cart.title')}</h2>
        <Elements stripe={stripePromise}>
          <CartCheckoutForm
            cart={cart}
            updateShippingMethod={updateShippingMethod}
            removeFromCart={removeFromCart}
            clearCart={clearCart}
            onSuccess={handleSuccess}
          />
        </Elements>
      </div>
    </div>
  );
};

export default Cart;
